import { defineStore } from 'pinia';
import { computed } from 'vue';

import type { DetailsStaticInfo } from '@libs/trip-planner-interfaces';

import type {
  ActivityStaticData,
  ActivityDynamicData,
} from '../../modules/activityDetails';

import { useTripStore } from './trip/tripStore';
import { useProductDetailStore } from './productDetail/productDetailStore';
import { useItineraryStore } from './itinerary/itineraryStore';

export const useActivityDetailsStore = defineStore(
  'ActivityDetailsStore',
  () => {
    const productDetailStore = useProductDetailStore();
    const tripStore = useTripStore();
    const itineraryStore = useItineraryStore();

    // Getters
    const activityDetailsStaticData = computed<ActivityStaticData | undefined>(
      () => {
        const staticActivityData =
          productDetailStore.destinationStaticProductDetailForActivity;

        if (!staticActivityData) {
          return;
        }

        const ingredientDate = itineraryStore.getIngredientDate(
          staticActivityData.ingredientId
        );

        if (!ingredientDate) {
          return;
        }

        const destination = productDetailStore.isDetailStaticInfo(
          staticActivityData.staticInfo
        )
          ? tripStore.getMainPolygonFullName(
              staticActivityData.ingredientId,
              staticActivityData.staticInfo
            )
          : undefined;

        return {
          parentIngredientId: staticActivityData.ingredientId,
          productCode: staticActivityData.productCode,
          staticInfo: staticActivityData.staticInfo as DetailsStaticInfo,
          isHearted: staticActivityData.isHearted,
          destination,
          numberOfNights: staticActivityData.numberOfNights,
          ingredientDate,
          scrollToAvailability: productDetailStore.shouldScrollToAvailability,
        };
      }
    );

    const activityDetailsDynamicData = computed<
      ActivityDynamicData | undefined
    >(() => {
      const activityDynamicData =
        productDetailStore.destinationDynamicProductDetailForActivity;

      if (!activityDynamicData) {
        return;
      }

      return {
        availability: activityDynamicData.availability,
        isAvailabilityLoading:
          productDetailStore.destinationStaticProductDetailForActivity
            ?.isAvailabilityLoading ?? false,
      };
    });

    return {
      // Getters
      activityDetailsStaticData,
      activityDetailsDynamicData,
    };
  }
);
